import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store"
import _ from "lodash"
import userInfoActions from "store/actions/UserInfoAction"
import LayoutWithModal from "components/Layout/LayoutWithModal"
import CommonTitle from "components/common/CommonTitle"
import CommonStepBar from "components/common/CommonStepBar"
import CommonHelp from "components/common/CommonHelp"
import ModalCommonTemplate from "components/Modal/ModalCommonTemplate"
import { modalSelector } from "components/Modal/ModalSelector"
import ButtonWithLicence from "components/Button/ButtonWithLicence"
import Loading from "components/Loading"
import { Box, Button, Container, Grid } from "@mui/material"
import RadioButtonCheckedSharpIcon from "@mui/icons-material/RadioButtonCheckedSharp"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import FormControl from "@mui/material/FormControl"
import {
  prefectures,
  nonFleetS,
  nonFleetF,
  driverScopeA,
  driverScopeB,
  driverScopeC,
  driverScopeD
} from "utils/ConditionData"
import ErrorMessage from "components/ErrorMessage"
import { isEmpty, isUnder20, isWrongCertificateColor } from "utils/Validators"
import { APP_URL, Colors, DriversLicenseColor, ErrorMessages, HAD_ACCIDENT } from "Const"
import { createRequestEasyCalc, getCompStartDate } from "utils/JsonManipulation"
import planActions from "store/actions/PlanAction"
import RightArrowButton from "components/Button/RightArrowButton"
import { isErrorInEasyCalculation } from "utils/JsonErrorCheck"
import GoBackButton from "components/Button/GoBackButton"
import isHttpError from "utils/HttpErrorCheck"
import isHttpMaintenance from "utils/HttpMaintenanceCheck"
import getEstimation from "apis/getEstimation"
import iconCheck from "images/icon-check.svg"
import iconUnCheck from "images/icon-uncheck.svg"
import compensationStartDateActions from "store/actions/CompensationStartDateAction"
import styles from "./Conditions.module.scss"

const DRIVER_SCOPE = {
  A: "Eﾃ", // 運転者限定特約（本人補償型）
  B: "Eﾔ", // 運転者限定特約（本人・配偶者・別居未婚の子補償型）
  C: "11", // 運転者限定特約（同居の子・使用人以外補償型）
  D: "EB" // 運転者限定なし特約（同居の子・使用人年齢条件設定型）
}
const initialDriverScope = [{ "IT-A自動車-特定特約コード": DRIVER_SCOPE.A }]
const initialYoungestAge = -1
const initialDistance = "0"
const PURPOSE = {
  EVERYDAY: "1",
  COMMUTE: "2",
  BUSINESS: "3"
}
interface DriverScopeIf {
  "IT-A自動車-特定特約コード": string
}
interface DriverSelectIf {
  spouse: boolean
  dependents: boolean
  unmarried: boolean
  married: boolean
  consent: boolean
  friend: boolean
  servant: boolean
}
/**
 * Step3 被保険者情報入力画面
 * 被保険者にかかる情報の入力画面
 * 車情報および被保険者情報を簡易見積もりAPIに連携
 */
const Conditions = () => {
  const initDrivers: DriverSelectIf = {
    spouse: false,
    dependents: false,
    unmarried: false,
    married: false,
    consent: false,
    friend: false,
    servant: false
  }
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const ageRef = React.createRef<HTMLHeadingElement>()
  const licenseRef = React.createRef<HTMLHeadingElement>()
  const purposeRef = React.createRef<HTMLHeadingElement>()
  const prefectureRef = React.createRef<HTMLHeadingElement>()
  const youngestAgeRef = React.createRef<HTMLHeadingElement>()
  const nonFleetRef = React.createRef<HTMLHeadingElement>()
  const causeAccidentRef = React.createRef<HTMLHeadingElement>()
  const distanceRef = React.createRef<HTMLHeadingElement>()
  const scrollToAgeRef = React.useCallback(() => {
    if (ageRef && ageRef.current) {
      ageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [ageRef])
  const scrollToLicenseRef = React.useCallback(() => {
    if (licenseRef && licenseRef.current) {
      licenseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [licenseRef])
  const scrollToPurposeRef = React.useCallback(() => {
    if (purposeRef && purposeRef.current) {
      purposeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [purposeRef])
  const scrollToPrefectureRef = React.useCallback(() => {
    if (prefectureRef && prefectureRef.current) {
      prefectureRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [prefectureRef])
  const scrollToYoungestAgeRef = React.useCallback(() => {
    if (youngestAgeRef && youngestAgeRef.current) {
      youngestAgeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [youngestAgeRef])
  const scrollToNonFleetRef = React.useCallback(() => {
    if (nonFleetRef && nonFleetRef.current) {
      nonFleetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [nonFleetRef])
  const scrollToCauseAccidentRef = React.useCallback(() => {
    if (causeAccidentRef && causeAccidentRef.current) {
      causeAccidentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [causeAccidentRef])
  const scrollToDistanceRef = React.useCallback(() => {
    if (distanceRef && distanceRef.current) {
      distanceRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [distanceRef])
  const [openModal, setOpenModal] = useState(false)
  const [modalContents, setModalContents] = useState(<ModalCommonTemplate />)
  const [showErrors, setShowErrors] = useState(false)
  const [age, setAge] = useState(-1)
  const [youngest, setYoungest] = useState(initialYoungestAge)
  const [prefecture, setPrefecture] = useState("")
  const [nonFleetGrade, setNonFleetGrade] = useState("-1")
  const [nonFleetValue, setNonFleetValue] = useState("-1")
  const [licenseColor, setLicenseColor] = useState(-1)
  const [purpose, setPurpose] = useState("")
  const [addDriverChecks, setAddDriverChecks] = useState(initDrivers)
  const [spouse, setSpouse] = useState(false)
  const [dependents, setDependents] = useState(false)
  const [unmarried, setUnmarried] = useState(false)
  const [married, setMarried] = useState(false)
  const [consent, setConsent] = useState(false)
  const [friend, setFriend] = useState(false)
  const [servant, setServant] = useState(false)
  const [driverScopeType, setDriverScopeType] = useState(initialDriverScope)
  const [disabledByAge, setDisabledByAge] = useState(false)
  const [subscribed, setSubscribed] = useState(true)
  const [causeAccident, setCauseAccident] = useState("")
  const [causeAccidentSelect, setCauseAccidentSelect] = useState("")
  const [isGradeDownAccident, setIsGradeDownAccident] = useState("0")
  const [distance, setDistance] = useState(initialDistance)
  const couponCode = useSelector((state: AppState) => state.couponCode)
  const [loading, setLoading] = useState(false)
  const contractorAgeFromState = useSelector((state: AppState) => state.userInfo.contractorAge)
  const youngestAgeFromState = useSelector((state: AppState) => state.userInfo.youngestAge)
  const licenceColorFromState = useSelector((state: AppState) => state.userInfo.licenceColor)
  const purposeFromState = useSelector((state: AppState) => state.userInfo.purpose)
  const distanceFromState = useSelector((state: AppState) => state.userInfo.distance)
  const locationNameFromState = useSelector((state: AppState) => state.userInfo.location)
  const hadAccidentsFromState = useSelector((state: AppState) => state.userInfo.hadAccidents)
  const nonFleetGradeFromState = useSelector((state: AppState) => state.userInfo.nonFleetGrade)
  const driverScopeFromState = useSelector((state: AppState) => state.userInfo.driverScope)
  const driverSelectFromState = useSelector((state: AppState) => state.userInfo.driverSelect)
  const hadAccidentSelectFromState = useSelector((state: AppState) => state.userInfo.hadAccidentSelect)
  const gradeDownAccidentFromState = useSelector((state: AppState) => state.userInfo.gradeDownAccident)
  const scrollCheck = () => {
    if (isEmpty(age)) scrollToAgeRef()
    else if (isEmpty(licenseColor)) scrollToLicenseRef()
    else if (isEmpty(purpose)) scrollToPurposeRef()
    else if (isEmpty(prefecture)) scrollToPrefectureRef()
    else if ((dependents || servant) && isEmpty(youngest)) scrollToYoungestAgeRef()
    else if (isEmpty(nonFleetGrade)) scrollToNonFleetRef()
    else if (isEmpty(causeAccident)) scrollToCauseAccidentRef()
    else if (distance === initialDistance) scrollToDistanceRef()
  }
  const licenseColorSelect = (select: string) => {
    switch (select) {
      case "blue":
        setLicenseColor(DriversLicenseColor.Blue)
        break
      case "green":
        setLicenseColor(DriversLicenseColor.Green)
        break
      default:
        setLicenseColor(DriversLicenseColor.Gold)
    }
  }
  const purposeSelect = (select: string) => {
    switch (select) {
      case PURPOSE.COMMUTE:
        setPurpose(PURPOSE.COMMUTE)
        break
      case PURPOSE.BUSINESS:
        setPurpose(PURPOSE.BUSINESS)
        break
      default:
        setPurpose(PURPOSE.EVERYDAY)
    }
  }
  const driverTypeCheck = (drivers: any) => {
    if (
      drivers.spouse === driverScopeA[0] &&
      drivers.dependents === driverScopeA[1] &&
      drivers.unmarried === driverScopeA[2] &&
      drivers.married === driverScopeA[3] &&
      drivers.consent === driverScopeA[4] &&
      drivers.friend === driverScopeA[5] &&
      drivers.servant === driverScopeA[6]
    ) {
      setDriverScopeType([{ "IT-A自動車-特定特約コード": DRIVER_SCOPE.A }])
    }
    driverScopeB.forEach((scope) => {
      if (
        drivers.spouse === scope[0] &&
        drivers.dependents === scope[1] &&
        drivers.unmarried === scope[2] &&
        drivers.married === scope[3] &&
        drivers.consent === scope[4] &&
        drivers.friend === scope[5] &&
        drivers.servant === scope[6]
      ) {
        setDriverScopeType([{ "IT-A自動車-特定特約コード": DRIVER_SCOPE.B }])
      }
    })
    driverScopeC.forEach((scope) => {
      if (
        drivers.spouse === scope[0] &&
        drivers.dependents === scope[1] &&
        drivers.unmarried === scope[2] &&
        drivers.married === scope[3] &&
        drivers.consent === scope[4] &&
        drivers.friend === scope[5] &&
        drivers.servant === scope[6]
      ) {
        setDriverScopeType([{ "IT-A自動車-特定特約コード": DRIVER_SCOPE.C }])
      }
    })
    driverScopeD.forEach((scope) => {
      if (
        drivers.spouse === scope[0] &&
        drivers.dependents === scope[1] &&
        drivers.unmarried === scope[2] &&
        drivers.married === scope[3] &&
        drivers.consent === scope[4] &&
        drivers.friend === scope[5] &&
        drivers.servant === scope[6]
      ) {
        setDriverScopeType([{ "IT-A自動車-特定特約コード": DRIVER_SCOPE.D }])
      }
    })
  }
  const driverSelect = (select: string) => {
    const drivers = addDriverChecks
    switch (select) {
      case "spouse":
        drivers.spouse = !drivers.spouse
        setSpouse(drivers.spouse)
        break
      case "dependents":
        // 未選択になった場合、使用人の方が選択されていないなら最若年運転者年齢を初期化
        if (drivers.dependents) {
          if (!drivers.servant) {
            setYoungest(initialYoungestAge)
          }
        }
        drivers.dependents = !drivers.dependents
        setDependents(drivers.dependents)
        break
      case "unmarried":
        drivers.unmarried = !drivers.unmarried
        setUnmarried(drivers.unmarried)
        break
      case "married":
        drivers.married = !drivers.married
        setMarried(drivers.married)
        break
      case "consent":
        drivers.consent = !drivers.consent
        setConsent(drivers.consent)
        break
      case "friend":
        drivers.friend = !drivers.friend
        setFriend(drivers.friend)
        break
      case "servant":
        // 使用人の方が未選択になった場合の処理
        if (drivers.servant) {
          // お子さま（同居）が選択されているかつ、被保険者年齢 <= 最若年運転者年齢の場合、最若年運転者年齢を初期化
          if (drivers.dependents) {
            if (age <= youngest) {
              setYoungest(initialYoungestAge)
            }
            // お子さま（同居）が選択されていない場合、最若年運転者年齢を初期化
          } else {
            setYoungest(initialYoungestAge)
          }
        }
        drivers.servant = !drivers.servant
        setServant(drivers.servant)
        break
      default:
    }
    setAddDriverChecks(drivers)
    driverTypeCheck(drivers)
  }
  const isNonFleetGrade20 = (grade: string) => {
    if (!subscribed) return false
    const selectedGrade = nonFleetF.find((item) => item[0] === grade)
    return selectedGrade && selectedGrade[1] === "20"
  }
  const NonFleetSelect = () => {
    const items = []
    const nonFleetList = subscribed ? nonFleetF : nonFleetS
    for (let i = 0; i < nonFleetList.length; i += 1) {
      items.push(
        <option key={i} value={i}>
          {nonFleetList[i][1]}
        </option>
      )
    }
    return (
      <>
        <h3 ref={nonFleetRef}>お見積りご希望のノンフリート等級</h3>
        <FormControl variant="outlined" className={styles.formNonFleet}>
          <Select
            native
            label="nonFleet"
            value={nonFleetValue}
            className={styles.nonFleet}
            onChange={(e: SelectChangeEvent) => {
              if (e.target.value === "-1") {
                setNonFleetGrade("")
                setNonFleetValue("-1")
              } else {
                setNonFleetValue(String(e.target.value))
                setNonFleetGrade(nonFleetList[Number(e.target.value)][0])
                const selectedGrade = nonFleetList[Number(e.target.value)][0]
                if (isNonFleetGrade20(selectedGrade) && causeAccidentSelect === HAD_ACCIDENT.PAST_1_YEAR) {
                  setCauseAccident("")
                  setIsGradeDownAccident("0")
                  setCauseAccidentSelect("")
                }
              }
            }}
          >
            {/* eslint-disable jsx-a11y/control-has-associated-label */}
            <option value={-1}>&nbsp;</option>
            {items}
          </Select>
        </FormControl>
        <ErrorMessage isUnselected={showErrors && isEmpty(nonFleetGrade)} />
        <Box className={styles.commonHelpWrap}>
          <CommonHelp
            text="ノンフリート等級とは"
            click={() => {
              setModalContents(modalSelector("nonfleet"))
              setOpenModal(true)
            }}
          />
          {!subscribed && (
            <CommonHelp
              text="７S等級（複数所有新規）になる条件は？"
              click={() => {
                setModalContents(modalSelector("gradeCondition"))
                setOpenModal(true)
              }}
            />
          )}
        </Box>
      </>
    )
  }
  const listButton = (dist: string, title: string, text1: string) => {
    return (
      <div
        role="button"
        tabIndex={0}
        onKeyPress={() => {}}
        onClick={() => {
          setDistance(dist)
        }}
      >
        <li className={distance === dist ? styles.selected : ""}>
          <RadioButtonCheckedSharpIcon />
          <Box>
            <h4>{title}</h4>
            <p>{text1}</p>
          </Box>
        </li>
      </div>
    )
  }
  const iconChecked = (check: boolean) => {
    return <img alt="check" className={styles.check} src={check ? iconCheck : iconUnCheck} />
  }
  const isAllowedToGoNextPage = () => {
    // 同居のお子さま or 使用人の方オプションが選択された場合のみ、最若年運転者年齢は必須項目
    const isYongestAgeEmpty = dependents || servant ? isEmpty(youngest) : false
    // 保険加入有の場合、前年走行距離は必須項目
    const isDistanceEmpty = subscribed ? distance === initialDistance : false
    const emptyError =
      isEmpty(age) ||
      isEmpty(licenseColor) ||
      isEmpty(purpose) ||
      isEmpty(prefecture) ||
      isEmpty(nonFleetGrade) ||
      isYongestAgeEmpty ||
      isDistanceEmpty
    // 契約者年齢は20歳以下の場合ゴールド選択不可
    const certificateError = isWrongCertificateColor(age, licenseColor)
    // 現在保険加入中の場合のみ表示
    const checkHadAccidents = subscribed ? isEmpty(causeAccident) : false
    if (emptyError || certificateError || checkHadAccidents) scrollCheck()
    return !emptyError && !certificateError && !checkHadAccidents
  }
  const { vehicleType, carModel, firstInspectionYear, firstInspectionMonth, carName, automaticBraking } = useSelector(
    (state: AppState) => state.qr
  )
  const UUID = useSelector((state: AppState) => state.uuid.uuid)
  const onSubmit = async (
    contractorAge: string,
    youngestAge: string,
    licenceColor: string,
    Pps: string,
    location: string,
    driverScope: Array<DriverScopeIf>,
    hadAccidents: string,
    NFG: string,
    gradeDownAccident: string,
    hadAccidentSelect: string
  ) => {
    setLoading(true)
    setShowErrors(true)
    if (isAllowedToGoNextPage()) {
      dispatch(userInfoActions.updateContractorAge(contractorAge))
      dispatch(userInfoActions.updateYoungestAge(youngestAge))
      dispatch(userInfoActions.updateLicenceColor(licenceColor))
      dispatch(userInfoActions.updatePurpose(purpose))
      dispatch(userInfoActions.updateDistance(distance))
      dispatch(userInfoActions.updateLocation(location))
      dispatch(userInfoActions.updateDriverScope(driverScope))
      dispatch(userInfoActions.updateDriverSelect(addDriverChecks))
      dispatch(userInfoActions.updateHadAccidents(hadAccidents))
      dispatch(userInfoActions.updateNonFleetGrade(nonFleetGrade))
      dispatch(userInfoActions.updateHasAEB(automaticBraking))
      dispatch(userInfoActions.updateGradeDownAccident(gradeDownAccident))
      dispatch(userInfoActions.updateHadAccidentSelect(hadAccidentSelect))
      const req = createRequestEasyCalc(
        contractorAge,
        youngestAge,
        licenceColor,
        vehicleType,
        carModel,
        firstInspectionYear,
        firstInspectionMonth,
        carName,
        automaticBraking,
        Pps,
        distance,
        location,
        hadAccidents,
        NFG,
        UUID,
        driverScope,
        couponCode.couponCode,
        gradeDownAccident
      )
      console.log("REQUEST（簡易試算API）:", req)
      const res = await getEstimation(req)
      console.log("RESPONSE（簡易試算API）:", res)
      if (isHttpMaintenance(res)) {
        // HTTP MAINTENANCE CHECK
        navigate(APP_URL.MAINTENANCE)
      } else if (isHttpError(res)) {
        // HTTP ERROR CHECK
        navigate(APP_URL.ERROR, {
          state: { api: "お見積り", message: "" }
        })
      } else if (isErrorInEasyCalculation(res)) {
        // Json ERROR CHECK
        const message = res.data.EASIMessage.EASIBody.ResultBlc.ResultMsg.replace(/\[EW.+\] /, "")
        const errorTag = res.data.EASIMessage.EASIBody.ResultBlc.ResultMsg.substring(0, 8)
        const MessageID = {
          modelTypeError: "[EW7001]",
          modelDateError: "[EW0059]"
        }
        if (errorTag === MessageID.modelTypeError) {
          navigate(APP_URL.ERROR, {
            state: { api: "お見積り", message: ErrorMessages.MODEL_TYPE_INCOMPATIBGLE }
          })
        } else if (errorTag === MessageID.modelDateError) {
          navigate(APP_URL.ERROR, {
            state: { api: "お見積り", message: ErrorMessages.MODEL_INSPECTION_DATE_INCOMPATIBLE }
          })
        } else {
          navigate(APP_URL.ERROR, {
            state: { api: "お見積り", message }
          })
        }
      } else {
        // 正常系
        dispatch(planActions.updatePlan(res.data))
        navigate(APP_URL.PLANS)
      }
    }
    setLoading(false)
  }
  useEffect(() => {
    if (!carName) {
      // キャッシュに車名の情報がなければ、トップページへ
      navigate(APP_URL.TOP)
    }
    if (contractorAgeFromState) setAge(Number(contractorAgeFromState))
    if (youngestAgeFromState) setYoungest(Number(youngestAgeFromState))
    if (licenceColorFromState) setLicenseColor(Number(licenceColorFromState))
    if (purposeFromState) setPurpose(purposeFromState)
    if (distanceFromState) setDistance(distanceFromState)
    if (locationNameFromState) setPrefecture(locationNameFromState)
    if (hadAccidentsFromState) setCauseAccident(hadAccidentsFromState)
    if (hadAccidentSelectFromState) setCauseAccidentSelect(hadAccidentSelectFromState)
    if (gradeDownAccidentFromState) setIsGradeDownAccident(gradeDownAccidentFromState)
    if (nonFleetGradeFromState) {
      setNonFleetGrade(nonFleetGradeFromState)
      nonFleetS.forEach((select, key) => {
        if (nonFleetGradeFromState === select[0]) {
          setSubscribed(false)
          setNonFleetValue(String(key))
        }
      })
      nonFleetF.forEach((select, key) => {
        if (nonFleetGradeFromState === select[0]) {
          setSubscribed(true)
          setNonFleetValue(String(key))
        }
      })
    }
    if (driverScopeFromState.length > 0) setDriverScopeType(driverScopeFromState)
    if (driverSelectFromState) {
      _.forEach(driverSelectFromState, (select, key) => {
        if (select) driverSelect(key)
      })
    }
    // step3画面表示したときの始期日をstateに設定
    const compensationStartDate = getCompStartDate()
    dispatch(compensationStartDateActions.updateCompensationStartDate(compensationStartDate.toString()))
  }, [])
  useEffect(() => {
    if (Number(age) === 18 || Number(age) === -1) {
      setDisabledByAge(true)
    } else {
      setDisabledByAge(false)
    }
  }, [age])
  return (
    <LayoutWithModal
      openModal={openModal}
      closeModal={() => {
        setOpenModal(false)
      }}
      innerContents={modalContents}
    >
      <Container maxWidth="md" className={styles.root}>
        <Loading loadingFlag={loading} />
        <CommonStepBar num={3} />
        <CommonTitle titles={["お車を使用される方について"]} read="以下の質問にご回答ください。" />
        <hr />
        <Box className={styles.ageFormWrapper}>
          <h3 ref={ageRef}>主に使用される方の年齢</h3>
          <FormControl variant="outlined" className={styles.formControlAge}>
            {(() => {
              const items = []
              for (let i = 18; i < 100; i += 1) {
                items.push(
                  <option key={i} value={i}>
                    {i}
                  </option>
                )
              }
              return (
                <Select
                  native
                  label="Age"
                  className={styles.age}
                  value={age.toString()}
                  onChange={(e: SelectChangeEvent) => {
                    setYoungest(initialYoungestAge)
                    setAge(Number(e.target.value))
                    if (isUnder20(Number(e.target.value))) {
                      setLicenseColor(-1)
                    }
                  }}
                >
                  <option aria-label="None" value={-1}>
                    &nbsp;
                  </option>
                  {items}
                </Select>
              )
            })()}
          </FormControl>
          <span className={styles.ageSideLabel}>歳</span>
        </Box>
        <ErrorMessage isUnselected={showErrors && isEmpty(age)} />
        <hr className={styles.grayLine} />
        <h3 ref={licenseRef}>主に使用される方の免許証の色</h3>
        <Box className={styles.selectLicenceWrap}>
          <Box
            onClick={() => {
              if (!isUnder20(age)) {
                licenseColorSelect("gold")
              }
            }}
            className={licenseColor === DriversLicenseColor.Gold ? styles.selected : ""}
            style={isUnder20(age) ? { filter: "grayscale(100%)", opacity: 0.6 } : {}}
          >
            <ButtonWithLicence color="gold" text="ゴールド" />
          </Box>
          <Box
            onClick={() => {
              licenseColorSelect("blue")
            }}
            className={licenseColor === DriversLicenseColor.Blue ? styles.selected : ""}
          >
            <ButtonWithLicence color="blue" text="ブルー" />
          </Box>
          <Box
            onClick={() => {
              licenseColorSelect("green")
            }}
            className={licenseColor === DriversLicenseColor.Green ? styles.selected : ""}
          >
            <ButtonWithLicence color="green" text="グリーン" />
          </Box>
        </Box>
        <ErrorMessage isUnselected={showErrors && isEmpty(licenseColor)} />
        <Box className={styles.commonHelpWrap}>
          <CommonHelp
            text="免許証の色を選択する場合の注意点"
            click={() => {
              setModalContents(modalSelector("license"))
              setOpenModal(true)
            }}
          />
        </Box>
        <hr className={styles.grayLine} />
        <h3 ref={purposeRef}>お車の主な使用目的</h3>
        <Box className={styles.selectButtonWrap}>
          <Button
            onClick={() => {
              purposeSelect(PURPOSE.EVERYDAY)
              if (driverScopeType.length === 0) {
                setDriverScopeType(initialDriverScope)
              }
              if (servant) {
                driverSelect("servant")
              }
            }}
            className={purpose === PURPOSE.EVERYDAY ? styles.selected : ""}
          >
            日常・レジャー
          </Button>
          <Button
            onClick={() => {
              purposeSelect(PURPOSE.COMMUTE)
              if (driverScopeType.length === 0) {
                setDriverScopeType(initialDriverScope)
              }
              if (servant) {
                driverSelect("servant")
              }
            }}
            className={purpose === PURPOSE.COMMUTE ? styles.selected : ""}
          >
            通勤・通学
          </Button>
          <Button
            onClick={() => {
              purposeSelect(PURPOSE.BUSINESS)
              if (driverScopeType.length === 0) {
                setDriverScopeType(initialDriverScope)
              }
            }}
            className={purpose === PURPOSE.BUSINESS ? styles.selected : ""}
          >
            業務使用
          </Button>
        </Box>
        <ErrorMessage isUnselected={showErrors && isEmpty(purpose)} />
        <Box className={styles.commonHelpWrap}>
          <CommonHelp
            text="使用目的とは"
            click={() => {
              setModalContents(modalSelector("purpose"))
              setOpenModal(true)
            }}
          />
          <CommonHelp
            text='通勤・通学のための"送迎"で利用している場合'
            click={() => {
              setModalContents(modalSelector("transfer"))
              setOpenModal(true)
            }}
          />
        </Box>
        <hr className={styles.grayLine} />
        <h3 ref={prefectureRef}>主にお車を使用される地域</h3>
        <FormControl variant="outlined" className={styles.formControl}>
          {(() => {
            const items = []
            for (let i = 1; i < prefectures.length + 1; i += 1) {
              items.push(
                <option key={i} value={`0${i}`.slice(-2)}>
                  {prefectures[i - 1]}
                </option>
              )
            }
            return (
              <Select
                native
                label="prefectures"
                className={styles.prefectures}
                value={prefecture}
                onChange={(e: SelectChangeEvent) => {
                  setPrefecture(`0${e.target.value}`.slice(-2))
                }}
              >
                <option aria-label="None" value={-1}>
                  &nbsp;
                </option>
                {items}
              </Select>
            )
          })()}
        </FormControl>
        <ErrorMessage isUnselected={showErrors && isEmpty(prefecture)} />
        <hr className={styles.grayLine} />
        <>
          <>
            <h4 className={styles.contractorTitle}>お車を運転される方（複数選択可）</h4>
            <Box className={styles.multiToggleButtonWrap}>
              <Button className={styles.selected}>
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(true)}
                  <span>本人</span>
                </span>
              </Button>
              <Button
                onClick={() => {
                  driverSelect("spouse")
                }}
                className={spouse ? styles.selected : ""}
              >
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(spouse)}
                  <span>配偶者</span>
                </span>
              </Button>
              <Button
                onClick={() => {
                  driverSelect("dependents")
                }}
                className={dependents ? styles.selected : ""}
              >
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(dependents)}
                  <span>お子さま</span>
                  <span>（同居）</span>
                  <span className={styles.Including}>その方の配偶者含む</span>
                </span>
              </Button>
              <Button
                onClick={() => {
                  driverSelect("unmarried")
                }}
                className={unmarried ? styles.selected : ""}
              >
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(unmarried)}
                  <span>お子さま</span>
                  <span>（別居・未婚）</span>
                </span>
              </Button>
              <Button
                onClick={() => {
                  driverSelect("married")
                }}
                className={married ? styles.selected : ""}
              >
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(married)}
                  <span>お子さま</span>
                  <span>（別居・既婚）</span>
                </span>
              </Button>
              <Button
                onClick={() => {
                  driverSelect("consent")
                }}
                className={consent ? styles.selected : ""}
              >
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(consent)}
                  <span>同居の親族</span>
                </span>
              </Button>
              <Button
                onClick={() => {
                  driverSelect("friend")
                }}
                className={friend ? styles.selected : ""}
              >
                <span className={styles.IncludingButtonWrap}>
                  {iconChecked(friend)}
                  <span>
                    別居の親族
                    <br />
                    友人・知人
                  </span>
                </span>
              </Button>
              {purpose === PURPOSE.BUSINESS && (
                <Button
                  onClick={() => {
                    driverSelect("servant")
                  }}
                  className={`${styles.ButtonLast} ${servant ? styles.selected : ""}`}
                >
                  <span className={styles.IncludingButtonWrap}>
                    {iconChecked(servant)}
                    <span>使用人の方</span>
                  </span>
                </Button>
              )}
            </Box>
            {purpose !== PURPOSE.BUSINESS &&
              dependents &&
              (() => {
                const items = []
                let maxAge = 0
                if (age > 18 && age < 30) {
                  maxAge = age
                } else if (age >= 30) {
                  maxAge = 30
                }
                for (let i = 18; i < maxAge; i += 1) {
                  items.push(
                    <option key={i} value={i}>
                      {i}
                    </option>
                  )
                }
                if (age > 30) {
                  items.push(<option value={30}>30～</option>)
                }
                return (
                  <>
                    <Box className={styles.ageFormWrapper}>
                      <h3 ref={youngestAgeRef}>お車を運転するお子さま（同居）の方で最も若い方の年齢</h3>
                      <FormControl
                        variant="outlined"
                        className={`${disabledByAge && styles.disabledByAge} ${styles.formControlAge}`}
                      >
                        <Select
                          native
                          label="YoungestAge"
                          className={styles.age}
                          value={youngest.toString()}
                          disabled={disabledByAge}
                          onChange={(e: SelectChangeEvent) => setYoungest(Number(e.target.value))}
                        >
                          <option aria-label="None" value={-1} />
                          {items}
                        </Select>
                      </FormControl>
                      <span className={styles.ageSideLabel}>歳</span>
                    </Box>
                    <ErrorMessage isUnselected={showErrors && dependents && isEmpty(youngest)} />
                    <p className={styles.annotation}>
                      設定した年齢未満の「
                      <span
                        role="button"
                        tabIndex={0}
                        onKeyPress={() => {}}
                        onClick={() => {
                          setModalContents(modalSelector("liveTogether"))
                          setOpenModal(true)
                        }}
                      >
                        同居のお子さま
                      </span>
                      」が運転中の事故は補償されませんので、ご注意ください。
                    </p>
                  </>
                )
              })()}
            {purpose === PURPOSE.BUSINESS &&
              (dependents || servant) &&
              (() => {
                const items = []
                if (servant) {
                  for (let i = 18; i < 30; i += 1) {
                    items.push(
                      <option key={i} value={i}>
                        {i}
                      </option>
                    )
                  }
                  items.push(<option value={30}>30～</option>)
                } else {
                  let maxAge = 0
                  if (age > 18 && age < 30) {
                    maxAge = age
                  } else if (age >= 30) {
                    maxAge = 30
                  }
                  for (let i = 18; i < maxAge; i += 1) {
                    items.push(
                      <option key={i} value={i}>
                        {i}
                      </option>
                    )
                  }
                  if (age > 30) {
                    items.push(<option value={30}>30～</option>)
                  }
                }
                return (
                  <>
                    <Box className={styles.ageFormWrapper}>
                      <h3 ref={youngestAgeRef}>お車を運転するお子さま（同居）または使用人の方で最も若い方の年齢</h3>
                      <FormControl
                        variant="outlined"
                        className={`${disabledByAge && styles.disabledByAge} ${styles.formControlAge}`}
                      >
                        <Select
                          native
                          label="YoungestAge"
                          className={styles.age}
                          value={youngest.toString()}
                          disabled={disabledByAge}
                          onChange={(e: SelectChangeEvent) => setYoungest(Number(e.target.value))}
                        >
                          <option aria-label="None" value={-1} />
                          {items}
                        </Select>
                      </FormControl>
                      <span className={styles.ageSideLabel}>歳</span>
                    </Box>
                    <ErrorMessage isUnselected={showErrors && (dependents || servant) && isEmpty(youngest)} />
                    <p className={styles.annotation}>
                      設定した年齢未満の「
                      <span
                        role="button"
                        tabIndex={0}
                        onKeyPress={() => {}}
                        onClick={() => {
                          setModalContents(modalSelector("liveTogether"))
                          setOpenModal(true)
                        }}
                      >
                        同居のお子さま
                      </span>
                      」または「
                      <span
                        role="button"
                        tabIndex={0}
                        onKeyPress={() => {}}
                        onClick={() => {
                          setModalContents(modalSelector("servant"))
                          setOpenModal(true)
                        }}
                      >
                        使用人の方
                      </span>
                      」が運転中の事故は補償されませんので、ご注意ください。
                    </p>
                  </>
                )
              })()}
            <Box className={styles.commonHelpWrap}>
              <CommonHelp
                text="「運転者の範囲」の選び方について"
                click={() => {
                  setModalContents(modalSelector("driverRangeSelection"))
                  setOpenModal(true)
                }}
              />
            </Box>
          </>
          <hr className={styles.grayLine} />
        </>
        <h3>お車の現在の自動車保険加入状況</h3>
        <Box className={styles.toggleButtonWrap}>
          <Button
            onClick={() => {
              setSubscribed(true)
              setNonFleetGrade("-1")
              setNonFleetValue("-1")
              setCauseAccident("")
            }}
            className={subscribed ? styles.selected : ""}
          >
            加入している
          </Button>
          <Button
            onClick={() => {
              setSubscribed(false)
              setNonFleetGrade("-1")
              setCauseAccident("0")
              setDistance("0")
              setNonFleetValue("-1")
              setCauseAccidentSelect("")
              setIsGradeDownAccident("0")
            }}
            className={!subscribed ? styles.selected : ""}
          >
            加入していない
          </Button>
        </Box>
        <hr className={styles.grayLine} />
        {/* 初期化のためDOMを別に切り出している */}
        {!subscribed && NonFleetSelect()}
        {subscribed && (
          <>
            {NonFleetSelect()}
            <hr className={styles.grayLine} />
            <h3 ref={causeAccidentRef}>保険金を請求したお車の事故</h3>
            <Box className={styles.toggleButtonFlexWrap}>
              <Button
                fullWidth
                onClick={() => {
                  setCauseAccident("0")
                  setIsGradeDownAccident("0")
                  setCauseAccidentSelect(HAD_ACCIDENT.NO_ACCIDENT)
                }}
                className={`${causeAccidentSelect === HAD_ACCIDENT.NO_ACCIDENT ? styles.selected : ""} 
                  ${styles.fullWidthButton}`}
              >
                事故なし
              </Button>
            </Box>
            <Box className={styles.toggleButtonFlexWrap}>
              <Button
                onClick={() => {
                  setCauseAccident("1")
                  setIsGradeDownAccident("0")
                  setCauseAccidentSelect(HAD_ACCIDENT.PAST_2_3_YEARS)
                }}
                className={causeAccidentSelect === HAD_ACCIDENT.PAST_2_3_YEARS ? styles.selected : ""}
              >
                過去２～３年以内に事故あり
              </Button>
              <Button
                onClick={() => {
                  if (!isNonFleetGrade20(nonFleetGrade)) {
                    setCauseAccident("1")
                    setIsGradeDownAccident("1")
                    setCauseAccidentSelect(HAD_ACCIDENT.PAST_1_YEAR)
                  }
                }}
                className={`
                  ${
                    causeAccidentSelect === HAD_ACCIDENT.PAST_1_YEAR && !isNonFleetGrade20(nonFleetGrade)
                      ? styles.selected
                      : ""
                  }
                  ${isNonFleetGrade20(nonFleetGrade) ? styles.disabled : ""}
                `.trim()}
              >
                過去１年以内に事故あり
              </Button>
            </Box>
            <ErrorMessage isUnselected={showErrors && isEmpty(causeAccident)} />
            <hr className={styles.grayLine} />
            <>
              <h3>過去１年間の走行距離</h3>
              <ul className={styles.distance}>
                {listButton("3000", "3,000km以下", "月間250km程度/1日10km未満")}
                {listButton("5000", "5,000km以下", "月間400km程度/1日15km前後")}
                {listButton("7000", "7,000km以下", "月間600km程度/1日20km前後")}
                {listButton("10000", "10,000km以下", "月間800km程度/1日30km前後")}
                {listButton("15000", "15,000km以下", "月間1,250km程度/1日40km前後")}
                {listButton("20000", "20,000km以下", "月間1,650km程度/1日50km前後")}
                {listButton("20001", "20,000km超", "月間1,650km以上/1日50km以上")}
              </ul>
              <ErrorMessage isUnselected={showErrors && distance === initialDistance} />
            </>
          </>
        )}
        <Grid ref={distanceRef} item xs={12} className={styles.nextButtonWrap}>
          <Box className={styles.commonButtonWrap}>
            <RightArrowButton
              color={Colors.Orange}
              arrowColor={Colors.White}
              text="次　へ"
              onClick={() => {
                onSubmit(
                  age.toString(),
                  youngest.toString(),
                  licenseColor.toString(),
                  purpose,
                  prefecture.toString(),
                  driverScopeType,
                  causeAccident,
                  nonFleetGrade,
                  isGradeDownAccident,
                  causeAccidentSelect
                )
              }}
              isSquareOnPC={false}
            />
          </Box>
        </Grid>
        <GoBackButton where={APP_URL.RESULT} />
      </Container>
    </LayoutWithModal>
  )
}
export default Conditions
